import {
  dateValid,
  dateValidMax,
  dateValidMin,
  germanZipValid,
  validateEmail,
} from "utils/validation";
import { SpeciesEnum } from "../../../../__generated__/globalTypes";
import { selectCurrentInsuranceByAnimalId } from "../store/selectors/insurance";
import { Step } from "./types";
import { addDays, isAfter, isBefore, isSameDay, subMonths } from "date-fns";
import { MAX_AGE_CAT, MAX_AGE_DOG } from "components-shared/insuranceData";
import { SignUpProductEnum } from "../flow/SignupFlow/types";
import {
  getEarliestStartDate,
  getLastAllowedStartDate,
  userAbove18,
} from "utils/insurance";
import { selectCustomer } from "../store/selectors/customer";

export const stepsGer: Step[] = [
  {
    index: 0,
    name: "species",
    isValid: ({ animal }) => Boolean(animal?.species),
    next: ({ animal, animals }) => {
      if (animals.length > 1) {
        return "name";
      }

      return animal?.species === "dog" ? "product" : "name";
    },
  },
  {
    index: 2,
    name: "product",
    isValid: ({ animal, ...state }) =>
      Boolean(selectCurrentInsuranceByAnimalId(state, animal?.id).product),
    next: () => "name",
  },
  {
    index: 3,
    name: "name",
    isValid: ({ animal }) => Boolean(animal?.name),
    next: () => "breed",
  },
  {
    index: 4,
    name: "breed",
    isValid: ({ animal }) => Boolean(animal?.breed),
    next: ({ animal }) =>
      animal?.breed?.id === "DogBreed-blandras" ? "mixed-breed" : "animal-sex",
  },
  {
    index: 5,
    name: "mixed-breed",
    next: () => "animal-sex",
    isValid: ({ animal }) => {
      return animal?.breed?.id === "DogBreed-blandras"
        ? !!animal?.mixedBreedFHalf && !!animal?.mixedBreedSHalf
        : !!animal?.breed;
    },
  },
  {
    index: 6,
    name: "animal-sex",
    next: () => "castration",
    isValid: ({ animal }) => Boolean(animal?.sex),
  },
  {
    index: 7,
    name: "castration",
    next: ({ animal }) =>
      animal?.species === SpeciesEnum.cat ? "animal-access" : "animal-birthday",
    isValid: ({ animal }) => Boolean(animal?.sexSituation),
  },
  {
    index: 8,
    name: "animal-access",
    next: () => "animal-birthday",
    isValid: ({ animal }) => {
      if (!animal) return false;

      return animal.species === SpeciesEnum.cat ? Boolean(animal.access) : true;
    },
  },
  {
    index: 9,
    name: "animal-birthday",
    next: () => "address",
    isValid: ({ animal }) => {
      if (!animal) return false;
      const { species, birthday } = animal;

      if (!birthday) {
        return false;
      }

      return (
        dateValid(birthday) &&
        dateValidMin(
          new Date(birthday),
          subMonths(
            new Date(),
            species === SpeciesEnum.dog ? MAX_AGE_DOG : MAX_AGE_CAT
          )
        ) &&
        dateValidMax(new Date(birthday), new Date())
      );
    },
  },
  {
    index: 10,
    name: "address",
    next: ({ animal, ...state }) => {
      // TODO: this isn't an animal step but still has a dependency on an animal.
      // Since liabiliy is excluded from the multipet to start with we can cheat a bit
      // const insurance = selectCurrentInsuranceByAnimalId(state, animal?.id);
      const insurance = state.animals[0].insurances[0];

      return insurance.product === SignUpProductEnum.liability
        ? "user-birthday"
        : "summary";
    },
    isValid: (state) => {
      const customer = selectCustomer(state);

      if (!customer.address?.zip) {
        return false;
      }

      return germanZipValid(customer.address.zip);
    },
  },
  {
    index: 11,
    name: "summary",
    next: () => "loading",
    isValid: () => true,
  },
  // Health and OP steps
  {
    index: 11,
    name: "loading",
    next: ({ animal, ...state }) => {
      // TODO: this isn't an animal step but still has a dependency on an animal.
      // Since liabiliy is excluded from the multipet to start with we can cheat a bit
      // const insurance = selectCurrentInsuranceByAnimalId(state, animal?.id);
      const insurance = state.animals[0].insurances[0];

      return insurance.product === SignUpProductEnum.liability
        ? "liability-quote-preview"
        : "quote-preview";
    },
    isValid: () => true,
  },
  {
    index: 12,
    name: "quote-preview",
    next: () => "health-questions",
    isValid: ({ animal, ...state }) => {
      if (!animal) return false;

      const { startDate } = selectCurrentInsuranceByAnimalId(state, animal.id);
      const { birthday } = animal;

      if (!startDate || !birthday) {
        return false;
      }

      return (
        dateValid(startDate) &&
        (isSameDay(getEarliestStartDate(birthday), new Date(startDate)) ||
          (isAfter(new Date(startDate), getEarliestStartDate(birthday)) &&
            isBefore(new Date(startDate), getLastAllowedStartDate())))
      );
    },
    previous: (state) => (state.metadata.isResumed ? "summary" : null),
  },
  {
    index: 13,
    name: "health-questions",
    next: () => "user-info",
    isValid: ({ animal, ...state }) => {
      if (!animal) return false;

      return animal.healthHistory.length > 0;
    },
  },
  // Liability specific steps
  {
    index: 14,
    name: "user-birthday",
    next: () => "summary-liability",
    isValid: (state) => {
      const customer = selectCustomer(state);

      if (!customer.birthdate) {
        return false;
      }

      return userAbove18(customer.birthdate);
    },
  },
  {
    index: 15,
    name: "summary-liability",
    next: () => "loading",
    isValid: () => true,
  },
  {
    index: 16,
    name: "liability-quote-preview",
    next: () => "previous-claims",
    isValid: ({ animal, ...state }) => {
      if (!animal) return false;

      const { birthday } = animal;
      const { startDate } = selectCurrentInsuranceByAnimalId(state, animal.id);

      if (!startDate || !birthday) {
        return false;
      }

      return (
        dateValid(startDate) &&
        isAfter(
          addDays(new Date(startDate), 1),
          getEarliestStartDate(birthday)
        ) &&
        isBefore(new Date(startDate), getLastAllowedStartDate())
      );
    },
    previous: (state) =>
      state.metadata.isResumed ? "summary-liability" : null,
  },
  {
    index: 17,
    name: "previous-claims",
    next: () => "cancelled-previously",
    isValid: ({ animal, ...state }) => {
      const { liabilityNumberOfClaimsHistory } =
        selectCurrentInsuranceByAnimalId(state, animal?.id);

      return !!(
        liabilityNumberOfClaimsHistory &&
        liabilityNumberOfClaimsHistory !== "three_or_more_claims"
      );
    },
  },
  {
    index: 18,
    name: "cancelled-previously",
    next: () => "user-info",
    isValid: ({ animal, ...state }) => {
      const { liabilityCancelledByPreviousInsuranceCompany } =
        selectCurrentInsuranceByAnimalId(state, animal?.id);

      return !!(
        liabilityCancelledByPreviousInsuranceCompany &&
        liabilityCancelledByPreviousInsuranceCompany !== "YES"
      );
    },
  },
  // Common steps cont.
  {
    index: 20,
    name: "user-info",
    next: ({ animals }) => {
      return animals[0].insurances[0].product === SignUpProductEnum.liability
        ? "draft-summary-liability"
        : "draft-summary";
    },
    isValid: (state) => {
      const customer = selectCustomer(state);

      return !!(
        validateEmail(customer.email) &&
        customer.firstName &&
        customer.lastName &&
        userAbove18(customer.birthdate ?? "") &&
        customer.address?.line1 &&
        customer.address.city &&
        customer.privacyAccepted &&
        customer.termsAccepted
      );
    },
  },
  {
    index: 20,
    name: "draft-summary-liability",
    next: () => "payment",
    isValid: () => true,
  },
  {
    index: 20,
    name: "draft-summary",
    next: () => "payment",
    isValid: () => true,
  },
  {
    index: 21,
    name: "payment",
    next: () => "thank-you",
    isValid: () => true,
  },
  {
    index: 22,
    name: "thank-you",
    next: () => "thank-you",
    isValid: () => true,
  },
];
